import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EMPTY, Observable } from "rxjs";
// eslint-disable-next-line rxjs/no-tap
import { expand, map, startWith, switchMap, take, tap } from "rxjs/operators";
import { SharedApiV2 } from "@capstone/mock-api";
import { PaginationV2 } from "src/app/core/pagination-v2";
import { headers, RequestUtils } from "src/app/core/request.service";
import {
  ApiUrl,
  OperationName,
  OperationRequestBody,
  OperationResponse,
  PathParamsOfOperation,
  QueryParamsOfOperation,
} from "src/utils/resource-url-v2/resource-url-v2-types";
import { UrlUtils } from "src/utils/resource-url-v2/resource-url-v2-utils";

/**
 * @deprecated Use {@link PrefixedRequestService} from
 * `src/app/core/prefixed-request.service` for new consumers.
 * Direct usage of this service is reserved for `PrefixedRequestService`
 * and `SitesService` only.
 */
@Injectable({ providedIn: "root" })
export class RequestV2Service {
  public constructor(private readonly httpClient: HttpClient) {}

  public apiBuildNumber: string | null = null;

  /**
   * Makes an HTTP GET request for a single item or the first page of a
   * collection, initiated with a null to signal loading/reloading.
   * @param resource The resource being requested.
   * @deprecated Use {@link PrefixedRequestService.get} instead.
   */
  public get<PathName extends ApiUrl, Method extends OperationName = "get">({
    prefix = "",
    path,
    pathParams,
    queryParams,
    headers,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    queryParams?: QueryParamsOfOperation<PathName, Method>;
    headers?: HttpHeaders;
  }): Observable<OperationResponse<PathName, Method> | null> {
    const apiUrl = UrlUtils.generateApiURL<
      Method,
      PathName,
      PathParamsOfOperation<PathName, Method>,
      QueryParamsOfOperation<PathName, Method>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    return this.httpClient
      .get<OperationResponse<PathName, Method>>(apiUrl, {
        headers,
        observe: "response",
      })
      .pipe(
        // There's probably a better way to do this with interceptors or
        // something, but for now we can pull the API build number off each
        // request to keep it updated and available for display in the footer.
        tap((response) => {
          this.apiBuildNumber = response.headers.get("API-Build-Number");
        }),
        map((response) => response.body ?? null),
        // TODO: remove this, the get method should only return the actual value and should not be responsible for
        // handling the loading state for the components by providing an initial null value.
        // This currently introduces unexpected behavior while converting the observable to a promise, and it forces us always to use the firstExistentValueFrom in the consumer services
        startWith(null),
      );
  }

  /** @deprecated Use {@link PrefixedRequestService.getBlob} instead. */
  public getBlob<
    PathName extends ApiUrl,
    Method extends OperationName = "get",
  >({
    prefix = "",
    path,
    pathParams,
    queryParams,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    queryParams?: QueryParamsOfOperation<PathName, Method>;
  }): Observable<Blob | null> {
    const apiUrl = UrlUtils.generateApiURL<
      Method,
      PathName,
      PathParamsOfOperation<PathName, Method>,
      QueryParamsOfOperation<PathName, Method>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    return this.httpClient
      .get(apiUrl, { headers, responseType: "blob" })
      .pipe(startWith(null));
  }

  /**
   * Makes an HTTP GET requests for the first page of a collection, initiated
   * with a null to signal loading/reloading. Subsequent pages will load based
   * on the load requests from the pagination controller.
   * @param args
   * @deprecated Use {@link PrefixedRequestService.getPaged} instead.
   */
  public getPaged<PathName extends ApiUrl>(
    args: GetPagedRequestArgs<PathName>,
  ): Observable<OperationResponse<PathName, "post"> | null> {
    const pagination = args.pagination;

    // Only reset if not preserving initial pagination values
    if (!args.shouldPreserveInitialPagination) {
      pagination.reset();
    }

    return this.post(args).pipe(
      // Since we don't control the pagination here, we have to update it as a
      // side-effect of our request in order to keep it in sync. Resetting it
      // here ensures the page info always represents the current resource URL's
      // pages (e.g. if filters or sorting change).

      tap((result) => {
        if (result) {
          if (!isPagedList(result)) {
            throw new Error(
              "'getPaged' method can only be used with paged lists.",
            );
          }
          pagination.reset(result);
        }
      }),
      // `expand` will emit the initial result but then also listen for new
      // request from the pagination control and emit those new pages as they're
      // requested.
      expand((result) => {
        if (!result) {
          // Ignore the loading indicator values (null).
          return EMPTY;
        }
        return pagination.requestNotifier.pipe(
          take(1),
          switchMap(() =>
            // Recall the same request with the new page number (encapsulated in its pagination handler).
            this.post(args).pipe(
              // Again, we have to update our page link info as a side-effect in
              // order to keep it in sync with the results.
              switchMap(async (nextResult) => {
                if (nextResult) {
                  if (!isPagedList(nextResult)) {
                    throw new Error(
                      "'getPaged' method can only be used with paged lists.",
                    );
                  }

                  await pagination.loadPage(nextResult);
                }
                return nextResult;
              }),
            ),
          ),
        );
      }),
    );
  }

  /** @deprecated Use {@link PrefixedRequestService.post} instead. */
  public post<PathName extends ApiUrl, Method extends OperationName = "post">({
    prefix = "",
    path,
    pathParams,
    queryParams,
    data,
  }: PostRequestArgs<PathName, Method>): Observable<OperationResponse<
    PathName,
    Method
  > | null> {
    const apiUrl = UrlUtils.generateApiURL<
      Method,
      PathName,
      PathParamsOfOperation<PathName, Method>,
      QueryParamsOfOperation<PathName, Method>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    const body = data.serialize();

    return this.httpClient
      .post<OperationResponse<PathName, Method>>(apiUrl, body, {
        headers: RequestUtils.getPostRequestHeaders(body, data),
      })
      .pipe(startWith(null));
  }

  /** @deprecated Use {@link PrefixedRequestService.put} instead. */
  public put<PathName extends ApiUrl, Method extends OperationName = "put">({
    prefix = "",
    path,
    pathParams,
    queryParams,
    data,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    queryParams?: QueryParamsOfOperation<PathName, Method>;
    data: PutRequestData<PathName>;
  }): Observable<OperationResponse<PathName, Method>> {
    const apiUrl = UrlUtils.generateApiURL<
      Method,
      PathName,
      PathParamsOfOperation<PathName, Method>,
      QueryParamsOfOperation<PathName, Method>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    const body = data.serialize();

    return this.httpClient.put<OperationResponse<PathName, Method>>(
      apiUrl,
      body,
      {
        headers: RequestUtils.getHttpHeaders(data, headers),
      },
    );
  }

  /** @deprecated Use {@link PrefixedRequestService.patch} instead. */
  public patch<
    PathName extends ApiUrl,
    Method extends OperationName = "patch",
  >({
    prefix = "",
    path,
    pathParams,
    data,
    options,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    data: PatchRequestData<PathName> | null;
    options?: PatchRequestOptions;
  }): Observable<OperationResponse<PathName, Method>> {
    const apiUrl = UrlUtils.generateApiURL<
      Method,
      PathName,
      PathParamsOfOperation<PathName, Method>,
      QueryParamsOfOperation<PathName, Method>
    >({
      prefix,
      path,
      pathParams,
    });

    const body = data && data.serialize();

    return this.httpClient.patch<OperationResponse<PathName, Method>>(
      apiUrl,
      body,
      { headers: RequestUtils.getHttpHeaders(data, headers), ...options },
    );
  }

  /** @deprecated Use {@link PrefixedRequestService.delete} instead. */
  public delete<
    PathName extends ApiUrl,
    Method extends OperationName = "delete",
  >({
    prefix = "",
    path,
    pathParams,
    queryParams,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    queryParams?: QueryParamsOfOperation<PathName, Method>;
  }): Observable<OperationResponse<PathName, Method>> {
    const apiUrl = UrlUtils.generateApiURL<
      Method,
      PathName,
      PathParamsOfOperation<PathName, Method>,
      QueryParamsOfOperation<PathName, Method>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    return this.httpClient.delete<OperationResponse<PathName, Method>>(apiUrl);
  }
}

type GetPagedRequestArgs<PathName extends ApiUrl> =
  PostRequestArgs<PathName> & {
    pagination: PaginationV2;
    /**
     * If true, preserves the initial pagination values (pageSize, currentPage)
     * instead of resetting them. Useful for custom pagination scenarios like
     * loading a specific number of items.
     * @default false
     */
    shouldPreserveInitialPagination?: boolean;
  };

interface PostRequestArgs<
  PathName extends ApiUrl,
  Method extends OperationName = "post",
> {
  prefix?: string;
  path: PathName;
  pathParams?: PathParamsOfOperation<PathName, Method>;
  queryParams?: QueryParamsOfOperation<PathName, Method>;
  data: PostRequestData<PathName>;
}

export interface PatchRequestOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: "body";
  params?:
    | HttpParams
    | {
        [param: string]:
          string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
}

export interface PostRequestData<PathName extends ApiUrl = ApiUrl> {
  version?: string;
  serialize(): OperationRequestBody<PathName, "post">;
}

export interface PutRequestData<PathName extends ApiUrl = ApiUrl> {
  version?: string;
  serialize(): OperationRequestBody<PathName, "put">;
}

export interface PatchRequestData<PathName extends ApiUrl = ApiUrl> {
  version?: string;
  serialize(): OperationRequestBody<PathName, "patch">;
}

function isPagedList<T>(result: object): result is SharedApiV2.PagedList<T> {
  return (
    "currentPage" in result &&
    typeof result.currentPage === "number" &&
    "totalPages" in result &&
    typeof result.totalPages === "number" &&
    "pageSize" in result &&
    typeof result.pageSize === "number" &&
    "totalCount" in result &&
    typeof result.totalCount === "number" &&
    "items" in result &&
    ((typeof result.items === "object" && Array.isArray(result.items)) ||
      result.items === null) &&
    "hasPrevious" in result &&
    typeof result.hasPrevious === "boolean" &&
    "hasNext" in result &&
    typeof result.hasNext === "boolean"
  );
}
